// SimpleTracker.js
//
// Generic tracker component used by Injury Reports and Maintenance Requests
// in Build 24. Both apps share nearly identical structure (list, filters,
// form, status changes), so we configure rather than duplicate.
//
// This is NOT used by Defect Tracker or Feature Requests — those keep their
// existing dedicated components.
//
// Config schema:
//   config = {
//     collectionName: string,
//     breadcrumbLabel: string,
//     breadcrumbIcon: string,
//     pageTitle: string,
//     entityNoun: string,        // "Injury Report" / "Maintenance Request"
//     entityNounPlural: string,
//     numberPrefix: string,      // 'IJ' for injuries, 'MR' for maintenance
//     statusOptions: string[],
//     statusColors: { [status]: hex },
//     extraFields: [{
//       key, label, type, options?, fullWidth?
//     }],
//     nextNumberFn: async (applicationName) => number
//   }

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection, addDoc, updateDoc, deleteDoc, doc, query,
  onSnapshot, serverTimestamp, orderBy, getDoc, arrayUnion
} from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { auth, db } from './firebase';
import { useTrackerData } from './hooks/useTrackerData';
import { formatDisplayNumber } from './utils/helpers';
import LoadingSpinner from './components/LoadingSpinner';
import './SimpleTracker.css';

function SimpleTracker({ config }) {
  const navigate = useNavigate();
  const { user, userProfile, authLoading, applications, role, isAdmin } = useTrackerData();

  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewOnly, setViewOnly] = useState(false);
  const [saving, setSaving] = useState(false);

  // Filters
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterApp, setFilterApp] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('displayNumber');
  const [sortOrder, setSortOrder] = useState('desc');

  // Form state — title/description are always present; extras come from config
  const blankForm = () => {
    const base = {
      title: '',
      description: '',
      applicationName: '',
      status: config.statusOptions[0] || 'Open'
    };
    config.extraFields.forEach(f => {
      base[f.key] = f.defaultValue ?? '';
    });
    return base;
  };
  const [formData, setFormData] = useState(blankForm());
  const [newUpdateText, setNewUpdateText] = useState('');

  useEffect(() => {
    document.title = `${config.pageTitle} - Wixon & Cross Desk Suite`;
  }, [config.pageTitle]);

  // Items listener
  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, config.collectionName), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [user, config.collectionName]);

  const openCreateForm = () => {
    setEditing(null);
    setViewOnly(false);
    setFormData(blankForm());
    setShowForm(true);
  };

  const openEditForm = (item) => {
    const canEditThis = item.createdBy === user.uid || isAdmin;
    setViewOnly(!canEditThis);
    setEditing(item);
    const data = {
      title: item.title || '',
      description: item.description || '',
      applicationName: item.applicationName || '',
      status: item.status || config.statusOptions[0]
    };
    config.extraFields.forEach(f => {
      data[f.key] = item[f.key] ?? f.defaultValue ?? '';
    });
    setFormData(data);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setViewOnly(false);
    setFormData(blankForm());
    setNewUpdateText('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.applicationName) {
      alert('Title and Application are required.');
      return;
    }
    setSaving(true);
    try {
      const dataToSave = {
        title: formData.title.trim(),
        description: formData.description,
        applicationName: formData.applicationName,
        status: formData.status,
        updatedAt: serverTimestamp(),
        updatedBy: user.email
      };
      config.extraFields.forEach(f => {
        dataToSave[f.key] = formData[f.key];
      });

      if (editing) {
        await updateDoc(doc(db, config.collectionName, editing.id), dataToSave);
      } else {
        let displayNumber = null;
        try {
          displayNumber = await config.nextNumberFn(formData.applicationName);
        } catch (err) {
          console.error('Could not reserve number:', err);
        }
        await addDoc(collection(db, config.collectionName), {
          ...dataToSave,
          ...(displayNumber !== null ? { displayNumber } : {}),
          updates: [],
          createdAt: serverTimestamp(),
          createdBy: user.uid,
          createdByEmail: user.email
        });
      }
      closeForm();
    } catch (err) {
      console.error('Error saving:', err);
      alert('Error saving: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
    try {
      await deleteDoc(doc(db, config.collectionName, item.id));
    } catch (err) {
      console.error('Error deleting:', err);
      alert('Error deleting.');
    }
  };

  const handleStatusChange = async (item, newStatus) => {
    if (newStatus === item.status) return;
    try {
      await updateDoc(doc(db, config.collectionName, item.id), {
        status: newStatus,
        updatedAt: serverTimestamp(),
        updatedBy: user.email
      });
    } catch (err) {
      console.error('Status update failed:', err);
      alert('Could not update status.');
    }
  };

  const handleAddUpdate = async () => {
    if (!editing) return;
    const text = newUpdateText.trim();
    if (!text) return;
    try {
      await updateDoc(doc(db, config.collectionName, editing.id), {
        updates: arrayUnion({
          timestamp: new Date(),
          description: text,
          userId: user.uid,
          userEmail: user.email
        }),
        updatedAt: serverTimestamp(),
        updatedBy: user.email
      });
      setNewUpdateText('');
      const fresh = await getDoc(doc(db, config.collectionName, editing.id));
      if (fresh.exists()) setEditing({ id: fresh.id, ...fresh.data() });
    } catch (err) {
      console.error('Error adding update:', err);
      alert('Could not add update.');
    }
  };

  const canEdit = (item) => item.createdBy === user?.uid || isAdmin;

  // ── Filtering & sorting ─────────────────────────────────────────────────
  const filteredItems = items.filter(i => {
    if (filterStatus !== 'All' && i.status !== filterStatus) return false;
    if (filterApp !== 'All' && i.applicationName !== filterApp) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const extraValues = config.extraFields.map(f => i[f.key]).filter(Boolean).join(' ');
      const hay = [i.title, i.description, extraValues].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  filteredItems.sort((a, b) => {
    let aVal, bVal;
    if (sortBy === 'createdAt' || sortBy === 'updatedAt') {
      aVal = a[sortBy]?.toDate?.() || new Date(0);
      bVal = b[sortBy]?.toDate?.() || new Date(0);
    } else if (sortBy === 'displayNumber') {
      aVal = typeof a.displayNumber === 'number' ? a.displayNumber : Number.POSITIVE_INFINITY;
      bVal = typeof b.displayNumber === 'number' ? b.displayNumber : Number.POSITIVE_INFINITY;
    } else {
      aVal = (a[sortBy] || '').toString().toLowerCase();
      bVal = (b[sortBy] || '').toString().toLowerCase();
    }
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSignOut = async () => {
    try { await signOut(auth); navigate('/'); }
    catch (err) { console.error(err); }
  };

  if (authLoading) return <LoadingSpinner message="Loading..." />;
  if (!user || !userProfile) return <LoadingSpinner message="Setting up..." />;

  return (
    <div className="simple-tracker-app">
      <header className="header">
        <div className="header-content">
          <div className="breadcrumb-nav-app">
            <button className="breadcrumb-item" onClick={() => navigate('/desktop')}>🖥️ Desktop</button>
            <span className="breadcrumb-separator">›</span>
            <button className="breadcrumb-item" onClick={() => navigate('/dashboard')}>📦 Dashboard</button>
            <span className="breadcrumb-separator">›</span>
            <span className="breadcrumb-item breadcrumb-current">
              {config.breadcrumbIcon} {config.breadcrumbLabel}
            </span>
          </div>
          <div className="header-actions">
            <div className="user-info-section">
              <span className="user-email">{user.email}</span>
              <span className="user-role-label">Role: <span className="role-badge">{role}</span></span>
            </div>
            <button className="btn btn-secondary" onClick={handleSignOut}>Sign Out</button>
          </div>
        </div>
      </header>

      <main className="tracker-main">
        <div className="tracker-toolbar">
          <h2>{config.entityNounPlural}</h2>
          <button className="btn btn-primary" onClick={openCreateForm}>
            + New {config.entityNoun}
          </button>
        </div>

        <div className="filter-bar">
          <input
            type="text" className="search-input"
            placeholder={`Search ${config.entityNounPlural.toLowerCase()}...`}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="filter-select">
            <option value="All">All Statuses</option>
            {config.statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filterApp} onChange={e => setFilterApp(e.target.value)} className="filter-select">
            <option value="All">All Applications</option>
            {applications.map(a => <option key={a.id} value={a.name}>{a.name}</option>)}
          </select>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="filter-select">
            <option value="displayNumber">Number</option>
            <option value="createdAt">Created Date</option>
            <option value="updatedAt">Updated Date</option>
            <option value="status">Status</option>
            <option value="title">Title</option>
          </select>
          <select value={sortOrder} onChange={e => setSortOrder(e.target.value)} className="filter-select">
            <option value="desc">Descending</option>
            <option value="asc">Ascending</option>
          </select>
        </div>

        <div className="tracker-stats">
          {config.statusOptions.map(s => (
            <div key={s} className="stat-pill">
              <span className="stat-label">{s}</span>
              <span className="stat-value">{filteredItems.filter(i => i.status === s).length}</span>
            </div>
          ))}
          <div className="stat-pill stat-pill-total">
            <span className="stat-label">Total</span>
            <span className="stat-value">{filteredItems.length}</span>
          </div>
        </div>

        {/* Card grid */}
        <div className="tracker-grid">
          {filteredItems.length === 0 && (
            <div className="tracker-empty">
              No {config.entityNounPlural.toLowerCase()} match your filters.
            </div>
          )}
          {filteredItems.map(item => (
            <div
              key={item.id}
              className="tracker-card tracker-card-clickable"
              onClick={() => openEditForm(item)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openEditForm(item);
                }
              }}
              style={{ borderTopColor: config.statusColors[item.status] || '#6b7280' }}
            >
              <div className="tracker-card-header">
                {typeof item.displayNumber === 'number' && (
                  <span className="tracker-card-seq">
                    {formatDisplayNumber(item, config.numberPrefix)}
                  </span>
                )}
                {item.autoCreatedFromIncident && (
                  <span className="tracker-card-auto" title="Auto-created from an incident">
                    🔗 from incident
                  </span>
                )}
                <span className="tracker-card-app">{item.applicationName}</span>
              </div>
              <h4 className="tracker-card-title">{item.title}</h4>
              {item.description && (
                <p className="tracker-card-desc">{item.description.slice(0, 140)}{item.description.length > 140 ? '…' : ''}</p>
              )}
              {/* Extra fields summary */}
              {config.extraFields.filter(f => item[f.key]).slice(0, 3).map(f => (
                <div key={f.key} className="tracker-card-extra">
                  <span className="tracker-extra-label">{f.label}:</span>{' '}
                  <span>{String(item[f.key]).slice(0, 60)}</span>
                </div>
              ))}
              {/* Footer — stopPropagation so clicking the select/buttons
                  doesn't also trigger the card's onClick (which would
                  reopen the modal). */}
              <div className="tracker-card-footer" onClick={(e) => e.stopPropagation()}>
                <select
                  value={item.status}
                  onChange={(e) => handleStatusChange(item, e.target.value)}
                  disabled={!canEdit(item)}
                  className="tracker-status-select"
                  onClick={(e) => e.stopPropagation()}
                >
                  {config.statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                {canEdit(item) ? (
                  <div className="tracker-card-actions">
                    <button onClick={(e) => { e.stopPropagation(); openEditForm(item); }} className="btn-icon" title="Edit">✏️</button>
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(item); }} className="btn-icon" title="Delete">🗑️</button>
                  </div>
                ) : (
                  <button onClick={(e) => { e.stopPropagation(); openEditForm(item); }} className="btn-icon" title="View">👁️</button>
                )}
              </div>
            </div>
          ))}
        </div>
      </main>

      {showForm && (
        <div className="modal-overlay" onClick={closeForm}>
          <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
            <h2>
              {!editing && `New ${config.entityNoun}`}
              {editing && viewOnly && `View ${config.entityNoun} (Read-Only)`}
              {editing && !viewOnly && `Edit ${config.entityNoun}`}
            </h2>
            {editing && typeof editing.displayNumber === 'number' && (
              <p className="incident-number-display">
                {formatDisplayNumber(editing, config.numberPrefix)}
              </p>
            )}
            {editing?.autoCreatedFromIncident && (
              <p className="auto-created-note">
                🔗 Auto-created from incident.
                {editing.sourceIncidentTitle && ` Source: "${editing.sourceIncidentTitle}"`}
              </p>
            )}
            {viewOnly && (
              <p className="view-only-note">
                👁️ You don't have permission to edit. Read-only view.
              </p>
            )}
            <form onSubmit={handleSave}>
              <fieldset disabled={viewOnly} className="form-fieldset">
              <div className="form-row">
                <div className="form-group form-group-full">
                  <label>Title *</label>
                  <input type="text" value={formData.title}
                    onChange={e => setFormData({ ...formData, title: e.target.value })} required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Application *</label>
                  <select
                    value={formData.applicationName}
                    onChange={e => setFormData({ ...formData, applicationName: e.target.value })}
                    required
                  >
                    <option value="">-- Select --</option>
                    {applications.map(a => <option key={a.id} value={a.name}>{a.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select value={formData.status}
                    onChange={e => setFormData({ ...formData, status: e.target.value })}>
                    {config.statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              {/* Render extra fields */}
              {config.extraFields.map(f => (
                <div key={f.key} className="form-row">
                  <div className={f.fullWidth ? 'form-group form-group-full' : 'form-group'}>
                    <label>{f.label}</label>
                    {f.type === 'select' ? (
                      <select value={formData[f.key] || ''}
                        onChange={e => setFormData({ ...formData, [f.key]: e.target.value })}>
                        <option value="">-- Select --</option>
                        {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : f.type === 'textarea' ? (
                      <textarea rows={f.rows || 3} value={formData[f.key] || ''}
                        onChange={e => setFormData({ ...formData, [f.key]: e.target.value })} />
                    ) : (
                      <input type={f.type || 'text'} value={formData[f.key] || ''}
                        onChange={e => setFormData({ ...formData, [f.key]: e.target.value })} />
                    )}
                  </div>
                </div>
              ))}

              <div className="form-row">
                <div className="form-group form-group-full">
                  <label>Description</label>
                  <textarea rows={4} value={formData.description}
                    onChange={e => setFormData({ ...formData, description: e.target.value })} />
                </div>
              </div>

              {editing && (
                <div className="updates-section">
                  <h3>Updates</h3>
                  <div className="updates-list">
                    {(editing.updates || []).length === 0 && (
                      <p className="updates-empty">No updates yet.</p>
                    )}
                    {(editing.updates || []).map((u, idx) => (
                      <div key={idx} className="update-item">
                        <div className="update-meta">
                          <span className="update-author">{u.userEmail}</span>
                          <span className="update-time">
                            {u.timestamp?.toDate?.().toLocaleString() ||
                              new Date(u.timestamp).toLocaleString()}
                          </span>
                        </div>
                        <div className="update-text">{u.description}</div>
                      </div>
                    ))}
                  </div>
                  {!viewOnly && (
                    <div className="update-add-row">
                      <textarea rows={2} placeholder="Add an update..."
                        value={newUpdateText}
                        onChange={e => setNewUpdateText(e.target.value)} />
                      <button type="button" className="btn btn-secondary"
                        onClick={handleAddUpdate} disabled={!newUpdateText.trim()}>
                        Add Update
                      </button>
                    </div>
                  )}
                </div>
              )}
              </fieldset>

              <div className="modal-actions">
                {!viewOnly && (
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving...' : (editing ? 'Save Changes' : `Create ${config.entityNoun}`)}
                  </button>
                )}
                <button type="button" className="btn btn-secondary" onClick={closeForm}>
                  {viewOnly ? 'Close' : 'Cancel'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default SimpleTracker;
